import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckIcon, DocumentDuplicateIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { TFunction } from 'i18next';

import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useRelationship } from 'services/repositories/relationships/relationships';
import { SUPPORT_EMAIL } from 'support/helpers/const/const';
import { getTradePartner, isResponsibleForInstantConnectivity } from 'support/helpers/relationships/relationships';
import { DocumentType, RelationshipV2 } from 'support/types';
import { useConnectionNavigation } from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { Alert } from 'components/Display/Alert';
import { SlideOver, SlideOverSubtitleSkeleton, SlideOverTitleSkeleton } from 'components/Display/SlideOver/SlideOver';
import { StepItem, Steps } from 'components/Display/Steps/Steps';
import { Divider } from 'components/Divider/Divider';
import { Button } from 'components/Form/Button';
import { Input } from 'components/Form/Input';
import { Translate } from 'components/Translate/Translate';

export const InstantConnectivitySlideOver = ({ open }: { open: boolean }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { partnerId, relationshipId } = useParams<{ partnerId: string; relationshipId: string }>();
  const { routeTo } = useConnectionNavigation();

  const { data: currentPartner, isLoading: isCurrentPartnerLoading } = useCurrentPartner();
  const { data: relationship, isLoading: isLoadingRelationship } = useRelationship({
    variables: { relationshipId },
    keepPreviousData: true,
    enabled: Boolean(relationshipId),
  });

  const isLoading = isCurrentPartnerLoading || isLoadingRelationship;
  const tradePartner = relationship && currentPartner && getTradePartner(relationship, currentPartner.id);
  // Only the supplier (the party responsible for forwarding) may open this window.
  const isSender = relationship?.sender_partner?.id === currentPartner?.id;
  const isResponsible = isResponsibleForInstantConnectivity(relationship?.message_type, isSender);
  const inboxEmail = relationship?.instant_connectivity?.inbox_email;

  const documentType = relationship ? t(`common:messageTypes.singular.${relationship.message_type}`) : '';
  const documentTypePlural = relationship ? t(`common:messageTypes.plural.${relationship.message_type}`) : '';
  const partnerName = tradePartner?.name ?? '';
  const isOrder = relationship?.message_type === DocumentType.order;

  const steps: Array<StepItem> = [
    {
      title: t('connections:instantConnectivitySlideOver.steps.copyAddress.title'),
      children: (
        <>
          <p>
            {isOrder
              ? t('connections:instantConnectivitySlideOver.steps.copyAddress.descriptionOrders', {
                  documentTypePlural,
                })
              : t('connections:instantConnectivitySlideOver.steps.copyAddress.description', {
                  documentTypePlural,
                  partnerName,
                })}
          </p>
          {inboxEmail && <CopyAddressField inboxEmail={inboxEmail} />}
        </>
      ),
    },
    {
      title: t('connections:instantConnectivitySlideOver.steps.autoForward.title'),
      children: (
        <p>
          <Translate
            i18nKey="connections:instantConnectivitySlideOver.steps.autoForward.description"
            values={{ documentTypePlural }}
          />
        </p>
      ),
    },
    ...getStopSendingSteps({ isOrder, relationship, documentTypePlural, partnerName, t }),
  ];

  const goBack = () =>
    navigate(routeTo('relationshipDetails', { partnerId: partnerId!, relationshipId: relationshipId! }));

  useEffect(() => {
    if (open && !isLoading && !isResponsible && relationshipId) {
      navigate(routeTo('relationshipDetails', { partnerId: partnerId!, relationshipId: relationshipId! }), {
        replace: true,
      });
    }
  }, [open, isLoading, isResponsible, navigate, partnerId, relationshipId, routeTo]);

  return (
    <SlideOver
      size="large"
      open={open}
      setOpen={goBack}
      title={
        isLoading ? (
          <SlideOverTitleSkeleton />
        ) : (
          t('connections:instantConnectivitySlideOver.title', { documentType, documentTypePlural })
        )
      }
      subtitle={isLoading ? <SlideOverSubtitleSkeleton /> : partnerName}
      headerImage={
        <div className="flex size-12 items-center justify-center rounded-md border border-gray-200">
          <EnvelopeIcon className="size-6 text-gray-500" aria-hidden="true" />
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <SlideOver.Section
          className="opacity-80"
          title={t('connections:instantConnectivitySlideOver.heading', { documentTypePlural })}
        >
          <p className="text-sm leading-5 text-gray-900">
            {isOrder
              ? t('connections:instantConnectivitySlideOver.introOrders', { documentTypePlural, partnerName })
              : t('connections:instantConnectivitySlideOver.intro', { documentType, documentTypePlural })}
          </p>
        </SlideOver.Section>

        <Steps forceOpen steps={steps} />

        <Divider />

        <Alert severity="info" title={t('connections:instantConnectivitySlideOver.whatHappensNext.title')}>
          <p>{t('connections:instantConnectivitySlideOver.whatHappensNext.description')}</p>
        </Alert>

        <p className="text-sm leading-5 text-gray-500">
          <Translate
            i18nKey="connections:instantConnectivitySlideOver.support"
            components={{ a: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-900 underline" /> }}
          />
        </p>
      </div>
    </SlideOver>
  );
};

const CopyAddressField = ({ inboxEmail }: { inboxEmail: string }) => {
  const { t } = useTranslation();
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  return (
    <div className="flex items-start gap-2">
      <Input wrapperClassName="flex-1" value={inboxEmail} readOnly aria-label={inboxEmail} />
      <Button
        variant="secondary"
        size="base"
        LeftIcon={isCopied ? CheckIcon : DocumentDuplicateIcon}
        onClick={() => copyToClipboard(inboxEmail)}
        analyticsId="instant_connectivity_copy_address"
      >
        {isCopied ? t('common:copied') : t('common:copy')}
      </Button>
    </div>
  );
};

const getStopSendingSteps = ({
  isOrder,
  relationship,
  documentTypePlural,
  partnerName,
  t,
}: {
  isOrder: boolean;
  relationship: RelationshipV2 | undefined;
  documentTypePlural: string;
  partnerName: string;
  t: TFunction;
}): Array<StepItem> => {
  // Asking them to stop emailing the partner is only safe once we deliver on their behalf. While
  // testing, forwarded documents are only used to validate the setup.
  const isDelivering = relationship?.status === 'ACTIVE' || relationship?.status === 'LIVE';

  return isOrder || !isDelivering
    ? []
    : [
        {
          title: t('connections:instantConnectivitySlideOver.steps.stopSending.title', {
            documentTypePlural,
            partnerName,
          }),
          children: <p>{t('connections:instantConnectivitySlideOver.steps.stopSending.description')}</p>,
        },
      ];
};
